import { z } from 'zod';
import type { PlanFeatures, PlanLimits } from './limits.js';

/**
 * Application provisioning — the panel's staff-facing surface over Coolify's
 * five application-creation endpoints and the application lifecycle
 * (deploy, logs, environment variables, persistent storage).
 *
 * The panel deliberately exposes a curated subset of Coolify's ~70 creation
 * parameters (see docs/adr/0006-application-provisioning.md for the split
 * between staff-only and future tenant-visible parameters). Everything here
 * is camelCase panel API surface; mapping to Coolify's snake_case happens in
 * the API's applications module.
 */

export const BUILD_PACKS = ['nixpacks', 'railpack', 'static', 'dockerfile', 'dockercompose'] as const;
export type BuildPack = (typeof BUILD_PACKS)[number];

/** How the application's code reaches Coolify — one per creation endpoint. */
export const APPLICATION_SOURCES = [
  'public',
  'github-app',
  'deploy-key',
  'dockerfile',
  'dockerimage',
] as const;
export type ApplicationSource = (typeof APPLICATION_SOURCES)[number];

// ── Create ──────────────────────────────────────────────────────────

export const healthCheckSchema = z.object({
  enabled: z.boolean().optional(),
  path: z.string().trim().max(255).optional(),
  port: z.string().trim().regex(/^\d+$/, 'Port must be numeric').optional(),
  host: z.string().trim().max(255).optional(),
  method: z.string().trim().max(10).optional(),
  scheme: z.enum(['http', 'https']).optional(),
  returnCode: z.number().int().min(100).max(599).optional(),
  responseText: z.string().max(500).optional(),
  interval: z.number().int().min(1).optional(),
  timeout: z.number().int().min(1).optional(),
  retries: z.number().int().min(1).optional(),
  startPeriod: z.number().int().min(0).optional(),
});
export type HealthCheckInput = z.infer<typeof healthCheckSchema>;

/** e.g. "3000" or "3000,8080" — Coolify's ports_exposes format. */
const portsExposesSchema = z
  .string()
  .trim()
  .regex(/^\d+(,\d+)*$/, 'Ports must be a comma-separated list of numbers');

const domainsSchema = z
  .array(
    z
      .string()
      .trim()
      .url('Domains must be full URLs, e.g. https://app.example.com')
      .regex(/^https?:\/\//i, 'Domains must use http:// or https://'),
  )
  .max(20);

const commonCreateFields = {
  subscriptionId: z.string().uuid(),
  /** Which Coolify server runs the application. */
  serverUuid: z.string().trim().min(1).max(100),
  name: z.string().trim().min(1).max(200),
  description: z.string().max(2000).nullish(),
  domains: domainsSchema.optional(),
  /** Let Coolify generate a domain (wildcard/sslip.io) when none is given. */
  autogenerateDomain: z.boolean().default(true),
  portsExposes: portsExposesSchema.optional(),
  healthCheck: healthCheckSchema.optional(),
  /** Queue a deployment immediately after creation. */
  instantDeploy: z.boolean().default(false),
};

const gitCreateFields = {
  gitRepository: z.string().trim().min(1).max(500),
  gitBranch: z.string().trim().min(1).max(250),
  gitCommitSha: z.string().trim().max(64).optional(),
  buildPack: z.enum(BUILD_PACKS),
  installCommand: z.string().trim().max(1000).optional(),
  buildCommand: z.string().trim().max(1000).optional(),
  startCommand: z.string().trim().max(1000).optional(),
  /** Repo subdirectory the build runs from (monorepos). */
  baseDirectory: z.string().trim().max(255).optional(),
  /** Directory served by the static build pack / nixpacks static sites. */
  publishDirectory: z.string().trim().max(255).optional(),
  /** Path to the Dockerfile when buildPack = dockerfile. */
  dockerfileLocation: z.string().trim().max(255).optional(),
  /** Path to the compose file when buildPack = dockercompose. */
  dockerComposeLocation: z.string().trim().max(255).optional(),
};

export const createApplicationSchema = z.discriminatedUnion('source', [
  z.object({ source: z.literal('public'), ...commonCreateFields, ...gitCreateFields }),
  z.object({
    source: z.literal('github-app'),
    /** Coolify GitHub App (uuid from GET /github-apps). */
    githubAppUuid: z.string().trim().min(1).max(100),
    ...commonCreateFields,
    ...gitCreateFields,
  }),
  z.object({
    source: z.literal('deploy-key'),
    /** Coolify private key (uuid from Security → Private Keys). */
    privateKeyUuid: z.string().trim().min(1).max(100),
    ...commonCreateFields,
    ...gitCreateFields,
  }),
  z.object({
    source: z.literal('dockerfile'),
    /** Raw Dockerfile content, built without a git repository. */
    dockerfile: z.string().min(1).max(50_000),
    ...commonCreateFields,
  }),
  z.object({
    source: z.literal('dockerimage'),
    dockerImageName: z.string().trim().min(1).max(500),
    dockerImageTag: z.string().trim().min(1).max(200).default('latest'),
    ...commonCreateFields,
  }),
]);
export type CreateApplicationInput = z.infer<typeof createApplicationSchema>;

// ── Update / lifecycle ──────────────────────────────────────────────

export const updateApplicationSchema = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  description: z.string().max(2000).nullish(),
  domains: domainsSchema.optional(),
  gitBranch: z.string().trim().min(1).max(250).optional(),
  buildPack: z.enum(BUILD_PACKS).optional(),
  installCommand: z.string().trim().max(1000).optional(),
  buildCommand: z.string().trim().max(1000).optional(),
  startCommand: z.string().trim().max(1000).optional(),
  baseDirectory: z.string().trim().max(255).optional(),
  publishDirectory: z.string().trim().max(255).optional(),
  portsExposes: portsExposesSchema.optional(),
  healthCheck: healthCheckSchema.optional(),
});
export type UpdateApplicationInput = z.infer<typeof updateApplicationSchema>;

export const deployApplicationSchema = z.object({
  /** Rebuild without the build cache. */
  force: z.boolean().default(false),
});
export type DeployApplicationInput = z.infer<typeof deployApplicationSchema>;

/**
 * Repair endpoint input: attach the Coolify UUID to a ledger row left
 * pending when Coolify created the app but the panel write failed.
 */
export const linkApplicationSchema = z.object({
  coolifyUuid: z.string().trim().min(1).max(100),
});
export type LinkApplicationInput = z.infer<typeof linkApplicationSchema>;

export const deploymentsQuerySchema = z.object({
  skip: z.coerce.number().int().min(0).default(0),
  take: z.coerce.number().int().min(1).max(50).default(10),
});
export type DeploymentsQuery = z.infer<typeof deploymentsQuerySchema>;

export const logsQuerySchema = z.object({
  lines: z.coerce.number().int().min(1).max(10_000).default(100),
});
export type LogsQuery = z.infer<typeof logsQuerySchema>;

// ── Scheduled tasks + maintenance ──────────────────────────────────

export const createScheduledTaskSchema = z.object({
  name: z.string().trim().min(1).max(200),
  command: z.string().trim().min(1).max(4000),
  frequency: z.string().trim().min(1).max(100),
  container: z.string().trim().max(255).nullish(),
  timeout: z.number().int().min(1).max(86_400).default(300),
  enabled: z.boolean().default(false),
});
export type CreateScheduledTaskInput = z.infer<typeof createScheduledTaskSchema>;

export const updateScheduledTaskSchema = createScheduledTaskSchema.partial().refine(
  (value) => Object.values(value).some((entry) => entry !== undefined),
  'At least one scheduled-task field is required',
);
export type UpdateScheduledTaskInput = z.infer<typeof updateScheduledTaskSchema>;

export const scheduledTaskExecutionsQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
});
export type ScheduledTaskExecutionsQuery = z.infer<typeof scheduledTaskExecutionsQuerySchema>;

export const applicationMaintenanceSchema = z.object({
  enabled: z.boolean(),
  reason: z.string().trim().max(500).nullish(),
});
export type ApplicationMaintenanceInput = z.infer<typeof applicationMaintenanceSchema>;

export interface ScheduledTaskDto {
  uuid: string;
  name: string;
  command: string;
  frequency: string;
  container: string | null;
  timeout: number;
  enabled: boolean;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface ScheduledTaskExecutionDto {
  id: number | null;
  taskUuid: string | null;
  status: string | null;
  message: string | null;
  startedAt: string | null;
  finishedAt: string | null;
}

export interface ApplicationMaintenanceDto {
  enabled: boolean;
  reason: string | null;
  enabledAt: string | null;
  enabledBy: string | null;
  replacement: 'stopped-application';
}

// ── Environment variables ───────────────────────────────────────────

export const createEnvVarSchema = z.object({
  key: z
    .string()
    .trim()
    .min(1)
    .max(255)
    .regex(/^[A-Za-z_][A-Za-z0-9_]*$/, 'Keys are letters, digits and underscores'),
  value: z.string().max(65_535),
  /** Also available to preview (PR) deployments. */
  isPreview: z.boolean().optional(),
  /** Treat the value literally — no variable interpolation. */
  isLiteral: z.boolean().optional(),
  isMultiline: z.boolean().optional(),
  /** Show the value only at creation; the panel masks it afterwards. */
  isShownOnce: z.boolean().optional(),
});
export type CreateEnvVarInput = z.infer<typeof createEnvVarSchema>;

/** Coolify's PATCH /envs replaces by key, same fields as create. */
export const updateEnvVarSchema = createEnvVarSchema;
export type UpdateEnvVarInput = z.infer<typeof updateEnvVarSchema>;

export const bulkEnvVarsSchema = z.object({
  data: z.array(createEnvVarSchema).min(1).max(200),
});
export type BulkEnvVarsInput = z.infer<typeof bulkEnvVarsSchema>;

// ── Persistent storage ──────────────────────────────────────────────

export const createStorageSchema = z.object({
  /** Docker volume name (Coolify prefixes/suffixes it per resource). */
  name: z.string().trim().min(1).max(255),
  mountPath: z
    .string()
    .trim()
    .min(1)
    .max(255)
    .regex(/^\//, 'Mount path must be absolute (start with /)'),
  /** Bind-mount a host path instead of a named volume. */
  hostPath: z.string().trim().max(255).regex(/^\//, 'Host path must be absolute').nullish(),
});
export type CreateStorageInput = z.infer<typeof createStorageSchema>;

export const updateStorageSchema = z.object({
  name: z.string().trim().min(1).max(255).optional(),
  mountPath: z.string().trim().min(1).max(255).regex(/^\//).optional(),
  hostPath: z.string().trim().max(255).regex(/^\//).nullish(),
});
export type UpdateStorageInput = z.infer<typeof updateStorageSchema>;

// ── Plan-limit → Coolify container-limit mapping ────────────────────

/**
 * Coolify's per-container limits derived from a subscription's effective
 * plan limits. "0" is Coolify's (Docker's) "unlimited". Every application
 * in a subscription gets the full subscription quota as its per-container
 * cap — the panel has nothing finer-grained until per-resource budgets
 * exist (ADR-0006).
 */
export function coolifyLimitsFromPlan(limits: PlanLimits): {
  limits_memory: string;
  limits_cpus: string;
} {
  return {
    limits_memory: limits.memory_mb == null ? '0' : `${limits.memory_mb}M`,
    limits_cpus: limits.cpu_cores == null ? '0' : String(limits.cpu_cores),
  };
}

// ── DTOs ────────────────────────────────────────────────────────────

/** Live application state read from Coolify (defensively parsed). */
export interface CoolifyApplicationDto {
  uuid: string;
  name: string;
  description: string | null;
  status: string | null;
  fqdn: string | null;
  buildPack: string | null;
  gitRepository: string | null;
  gitBranch: string | null;
  installCommand: string | null;
  buildCommand: string | null;
  startCommand: string | null;
  baseDirectory: string | null;
  publishDirectory: string | null;
  portsExposes: string | null;
  limitsMemory: string | null;
  limitsCpus: string | null;
  healthCheck: {
    enabled: boolean;
    path: string | null;
    port: string | null;
    method: string | null;
    returnCode: number | null;
    interval: number | null;
    timeout: number | null;
    retries: number | null;
    startPeriod: number | null;
  };
  dockerRegistryImageName: string | null;
  dockerRegistryImageTag: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface ApplicationDetailDto {
  /** The ledger row (panel identity, quota accounting). */
  resource: {
    id: string;
    subscriptionId: string;
    name: string;
    coolifyUuid: string | null;
    status: string;
    details: Record<string, unknown> | null;
    createdAt: string;
  };
  subscription: {
    id: string;
    name: string;
    clientId: string;
    clientName: string;
    status: string;
  };
  /** What the subscription's plan allows — the UI hides gated tabs. */
  features: PlanFeatures;
  /** Live Coolify state; null when unlinked or Coolify is unreachable. */
  live: CoolifyApplicationDto | null;
  /** Why `live` is null (unreachable, not linked, …). */
  liveError: string | null;
}

export interface DeploymentDto {
  deploymentUuid: string;
  status: string;
  commit: string | null;
  commitMessage: string | null;
  forceRebuild: boolean;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface DeploymentLogLineDto {
  output: string;
  type: string;
  timestamp: string | null;
  hidden: boolean;
}

export interface DeploymentDetailDto extends DeploymentDto {
  logs: DeploymentLogLineDto[];
}

export interface EnvVarDto {
  uuid: string;
  key: string;
  /** null when the variable is shown-once (value never re-displayed). */
  value: string | null;
  isPreview: boolean;
  isLiteral: boolean;
  isMultiline: boolean;
  isShownOnce: boolean;
  updatedAt: string | null;
}

export interface StorageDto {
  uuid: string;
  name: string;
  mountPath: string;
  hostPath: string | null;
}

export interface GithubAppDto {
  id: number;
  uuid: string | null;
  name: string;
}

export interface GitRepositoryDto {
  id: number;
  name: string;
  fullName: string;
  defaultBranch: string | null;
  isPrivate: boolean;
}

export interface GitBranchDto {
  name: string;
}

export interface RuntimeLogsDto {
  logs: string;
}
